import { BookData, BookGeometry } from '../interfaces/book-data.interface';
import { SpreadData, SpreadRenderData } from '../interfaces/spread-data.interface';
import { CollageData } from '../interfaces/collage-data.interface';
import { CoverPrintSizes, PrintSizes } from '../interfaces/print-sizes.interface';
import { getAllDrawableImages } from './collage-queries';
import { eachFrameWithPersonalFlag, isForeignSoloImage, isPersonalSpread, isVignetteSurface } from './personal-spread-queries';
import { assignedPhotoFromImageAlbum, imageAlbumFromAssignedPhoto } from '../normalizer/boxes/assigned-photo';
import { ImageFrameRole } from '@lite-workspace/dto-fotoclass-templates';
import type { AssignedPhotoPayload } from '@lite-workspace/dto-fotoclass-templates';
import type { ImageAlbum } from '../interfaces/image-album.interface';
import type { DrawableImageData } from '../interfaces/drawable-data.interface';
import { BookFormat } from '../interfaces/book-format.type';
import { PaddingLimits } from '../interfaces/padding-limits.interface';

export type { BookFormat };

/** Book format is determined by the aspect ratio of a single page (width / 2 / height). */
export function getBookFormat(book: BookGeometry): BookFormat {
  const aspectRatio = book.width / 2 / book.height;
  if (aspectRatio < 0.9) {
    return 'vertical';
  }

  if (aspectRatio > 1.1) {
    return 'horizontal';
  }

  return 'square';
}

/** Форматов ровно три — вертикальный, квадратный, горизонтальный; сравнивается ТОЛЬКО ориентация страницы.
 *  Сантиметры и точные пропорции не важны: 30×20, 29×20, 30×22 — вертикальные, 30×30 и 23×23 — квадратные.
 *  Ради этого допуск ±10% по аспекту (width / 2 / height), а не точное равенство. */
export function resolveSpreadFormatFromPrintSizes(printSizes: Pick<PrintSizes, 'width' | 'height'>): BookFormat {
  const aspectRatio = printSizes.width / 2 / printSizes.height;
  if (aspectRatio < 0.9) return 'vertical';
  if (aspectRatio > 1.1) return 'horizontal';
  return 'square';
}

/** Image IDs used across all spreads and the cover (cover is a separate `book.cover` field, collected explicitly). */
export function getUsedImageIds(book: BookData): string[] {
  const sources: { collages?: readonly CollageData[] }[] = [...book.spreads, book.cover];
  return sources.reduce<string[]>((acc, source) => {
    const ids = (source.collages ?? []).reduce<string[]>((colAcc, collage) => {
      const imageIds = getAllDrawableImages(collage)
        .map(d => d.image?.id)
        .filter((id): id is string => id !== undefined);
      return colAcc.concat(imageIds);
    }, []);
    return acc.concat(ids);
  }, []);
}

/**
 * Deduplicated image keys of the whole book (cover + spreads); missing model/pieces yield [] (fail-closed).
 * `ownerPersonId` set = the album owner's own archive: a foreign solo portrait on a personal spread is skipped.
 */
export function collectBookImageKeys(
  model: BookData | null | undefined,
  byFilename = false,
  ownerPersonId?: string | null,
): string[] {
  const keys = new Set<string>();
  eachBookImage(model, ownerPersonId, image => keys.add(byFilename ? image.filename : image.id));
  return [...keys];
}

/** Image ids of the book by selection category (cover / vignette / portraits / groups) — same set as `collectBookImageKeys`. */
export interface BookImagesByRole {
  cover?: string;
  vignette?: string;
  portraits: string[];
  groups: string[];
}

/**
 * Category = the frame's own role: the cover surface → cover, a vignette surface → vignette, «Мой портрет» → portraits.
 * Anything else — other roles, no role, a second cover/vignette photo — lands in groups: no photo is dropped.
 */
export function collectBookImagesByRole(model: BookData | null | undefined, ownerPersonId?: string | null): BookImagesByRole {
  const result: BookImagesByRole = { portraits: [], groups: [] };
  const seen = new Set<string>();
  eachBookImage(model, ownerPersonId, (image, frame, surface) => {
    if (seen.has(image.id)) return;
    seen.add(image.id);
    const role = frame.frameRole ?? (frame as { params?: { frameRole?: ImageFrameRole } }).params?.frameRole;
    if (surface === model?.cover && !result.cover) result.cover = image.id;
    else if (isVignetteSurface(surface) && !result.vignette) result.vignette = image.id;
    else if (role === ImageFrameRole.OWNER_PORTRAIT) result.portraits.push(image.id);
    else result.groups.push(image.id);
  });
  return result;
}

function eachBookImage(
  model: BookData | null | undefined,
  ownerPersonId: string | null | undefined,
  visit: (image: ImageAlbum, frame: DrawableImageData, surface: SpreadData) => void,
): void {
  if (!model) return;
  const visitSurface = (surface?: SpreadData): void => {
    if (!surface) return;
    const skipForeignSolo = !!ownerPersonId && isPersonalSpread(surface);
    // Both persist shapes: a stored book keeps `boxes` with the photo in `params.assignedPhoto`.
    eachFrameWithPersonalFlag(surface, frame => {
      const assigned = (frame as { params?: { assignedPhoto?: AssignedPhotoPayload | null } }).params?.assignedPhoto;
      const image = frame.image ?? (assigned ? imageAlbumFromAssignedPhoto(assigned) : null);
      if (!image || (skipForeignSolo && isForeignSoloImage(image, ownerPersonId))) return;
      visit(image, frame, surface);
    });
  };

  visitSurface(model.cover);
  model.spreads?.forEach(visitSurface);
}

/**
 * A book photo overwritten by a new file: every frame holding `oldId` (both stored shapes) and the `images`
 * bin take `image`. The same model comes back when nothing held the photo — nothing to save.
 */
export function replacePhotoInModel(model: BookData, oldId: string, image: ImageAlbum): BookData {
  const next = structuredClone(model);
  let hit = false;
  const visitSurface = (surface?: SpreadData): void => {
    if (!surface) return;
    eachFrameWithPersonalFlag(surface, drawable => {
      const frame = drawable as { image: ImageAlbum | null; params?: { assignedPhoto?: AssignedPhotoPayload | null } };
      if (frame.image?.id === oldId) {
        frame.image = image;
        hit = true;
      }

      const assigned = frame.params?.assignedPhoto;
      if (frame.params && assigned?.photoId === oldId) {
        frame.params.assignedPhoto = assignedPhotoFromImageAlbum(image);
        hit = true;
      }
    });
  };

  visitSurface(next.cover);
  next.spreads?.forEach(visitSurface);
  const binHit = next.images?.some(entry => entry.id === oldId) ?? false;
  if (!hit && !binHit) return model;
  return binHit ? { ...next, images: next.images.map(entry => (entry.id === oldId ? image : entry)) } : next;
}

/**
 * Aspect ratio of the spread page content area (excluding cut/bleed).
 * Full spread width is `book.width` (two pages), so each page is `book.width / 2` minus cut.
 * Replicates Book.spreadAspectRatio — but note that property used full width (not /2).
 * The original: (width - cut_horizontal * 2) / (height - cut_vertical * 2)
 */
export function getSpreadAspectRatio(book: BookGeometry): number {
  return (book.width - book.cut_horizontal * 2) / (book.height - book.cut_vertical * 2);
}

/**
 * Aspect ratio of the cover content area (excluding wrap).
 * Falls back to half of the spread aspect ratio if cover sizes are zero.
 * Replicates Book.coverAspectRatio.
 */
export function getCoverAspectRatio(book: BookGeometry): number {
  const wrapH = book.cover_sizes?.wrap_horizontal ?? 0;
  const wrapV = book.cover_sizes?.wrap_vertical ?? 0;
  return (book.cover_sizes?.width - wrapH * 2) / (book.cover_sizes?.height - wrapV * 2) || getSpreadAspectRatio(book) / 2;
}

export function getSpread(book: BookData, spreadId: number): SpreadData | null {
  if (book.cover.id === spreadId) return book.cover;
  return book.spreads.find(s => s.id === spreadId) ?? null;
}

export function getCollage(book: BookData, spreadId: number, collageIndex: number): CollageData | null {
  const spread = getSpread(book, spreadId);
  if (!spread) return null;
  return spread.collages?.[collageIndex] ?? null;
}

export function getPrintSizes(book: BookGeometry): PrintSizes {
  return {
    dpi: book.dpi,
    width: book.width,
    height: book.height,
    safe_horizontal: book.safe_horizontal,
    safe_vertical: book.safe_vertical,
    cut_horizontal: book.cut_horizontal,
    cut_vertical: book.cut_vertical,
    ...(book.fold_bands && { fold_bands: book.fold_bands }),
  };
}

const INCH_MM = 25.4;

export function pxToMm(px: number, dpi: number): number {
  if (!isFinite(px) || !isFinite(dpi) || dpi <= 0) return 0;
  return (px / dpi) * INCH_MM;
}

export function mmToPx(mm: number, dpi: number): number {
  if (!isFinite(mm) || !isFinite(dpi) || dpi <= 0) return 0;
  return (mm / INCH_MM) * dpi;
}

export function getPrintHeightMm(book: BookGeometry): number {
  return pxToMm(book.height, book.dpi);
}

export function getPrintWidthMm(book: BookGeometry): number {
  return pxToMm(book.width, book.dpi);
}

export function getCoverPrintSizes(book: BookGeometry): PrintSizes {
  return book.cover_sizes;
}

/** Холст обложки — вылет-заворот плюс корешок, поэтому половина ширины на ней не является страницей. */
export function isCoverPrintSizes(printSizes: PrintSizes): printSizes is CoverPrintSizes {
  return 'spine_width' in printSizes;
}

export function toSpreadRenderData(spread: SpreadData, printSizes: PrintSizes): SpreadRenderData {
  return { ...spread, printSizes };
}

export const DEFAULT_PADDING_LIMITS_MM: PaddingLimits = {
  gap: { min: 0, max: 50 },
  outer: { min: 0, max: 25 },
  spine: { min: 0, max: 50 },
};

export const DEFAULT_PHOTO_SPACING_MM = 2;
// 0 = a full-bleed page photo meets the fold with no white band; the photographer sets «Центральный» by hand.
export const DEFAULT_SPINE_MARGIN_MM = 0;

export function mmToSpreadPercent(mm: number, heightMm: number): number {
  return heightMm > 0 ? (mm / heightMm) * 100 : 0;
}

// A zero value counts as unset — a 0 gap is never an intent, it sticks photos together.
export function coalesceZeroSpacing(value: number | null | undefined, fallback: number): number {
  return value != null && isFinite(value) && value > 0 ? value : fallback;
}

// Once the user has set album padding themselves, a zero IS an intent and must survive as-is.
// A negative never is — it would expand frames into each other, so it still coalesces to the default.
export function coalesceSpacing(value: number | null | undefined, fallback: number, userSet: boolean | undefined): number {
  if (userSet) return value != null && isFinite(value) && value >= 0 ? value : fallback;
  return coalesceZeroSpacing(value, fallback);
}

export function getPaddingLimits(book: BookGeometry | null | undefined): PaddingLimits {
  return book?.padding_limits ?? DEFAULT_PADDING_LIMITS_MM;
}

/**
 * Размер «безопасной зоны» вдоль внешнего края страницы в миллиметрах.
 *
 * Возвращает максимум из горизонтального и вертикального safe-полей — outer margin
 * применяется по всему периметру разворота, и чтобы фото не сжалось auto-snap'ом в bleed
 * хотя бы по одной оси, значение должно быть консервативным (большее из двух).
 */
export function getOuterSafeMm(book: BookGeometry | null | undefined): number {
  if (!book) return 0;
  const safeH = pxToMm(book.safe_horizontal, book.dpi);
  const safeV = pxToMm(book.safe_vertical, book.dpi);
  return Math.max(safeH, safeV);
}
